import React, { useEffect, useState } from "react";
import { Input, Modal, Select } from "antd";

import { errMsg } from "../../helpers/errMsg.helper";

import type {
  CustomField,
  CustomFieldAppliesTo,
  CustomFieldType,
} from "../../../../core/data/redux/projects/types";

interface CustomFieldEditorProps {
  open: boolean;
  initialField?: CustomField | null;
  onSubmit: (payload: {
    name: string;
    field_type: CustomFieldType;
    applies_to: CustomFieldAppliesTo;
    options: any[] | null;
  }) => Promise<void>;
  onCancel: () => void;
  submitting?: boolean;
}

const FIELD_TYPE_OPTIONS: { value: CustomFieldType; label: string }[] = [
  { value: "TEXT", label: "Text" },
  { value: "NUMBER", label: "Number" },
  { value: "DATE", label: "Date" },
  { value: "SELECT", label: "Select (single choice)" },
  { value: "MULTISELECT", label: "Multi-select" },
  { value: "USER", label: "User" },
];

const APPLIES_TO_OPTIONS: {
  value: CustomFieldAppliesTo;
  label: string;
}[] = [
    { value: "ALL", label: "All issue types" },
    { value: "TASK", label: "Task" },
    { value: "BUG", label: "Bug" },
    { value: "STORY", label: "Story" },
    { value: "EPIC", label: "Epic" },
    { value: "SUBTASK", label: "Subtask" },
  ];

const CustomFieldEditor: React.FC<CustomFieldEditorProps> = ({
  open,
  initialField,
  onSubmit,
  onCancel,
  submitting = false,
}) => {
  const isEdit = Boolean(initialField);
  const [name, setName] = useState("");
  const [fieldType, setFieldType] = useState<CustomFieldType>("TEXT");
  const [appliesTo, setAppliesTo] = useState<CustomFieldAppliesTo>("ALL");
  const [optionsText, setOptionsText] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (initialField) {
      setName(initialField.name);
      setFieldType(initialField.field_type);
      setAppliesTo(initialField.applies_to);
      setOptionsText(
        Array.isArray(initialField.options)
          ? initialField.options
            .map((o) => (typeof o === "string" ? o : o?.label || o?.value || ""))
            .join("\n")
          : ""
      );
    } else {
      setName("");
      setFieldType("TEXT");
      setAppliesTo("ALL");
      setOptionsText("");
    }
    setError(null);
  }, [open, initialField]);

  const isOptionType = fieldType === "SELECT" || fieldType === "MULTISELECT";

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError("Field name is required");
      return;
    }
    let options: any[] | null = null;
    if (isOptionType) {
      const lines = optionsText
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      if (lines.length === 0) {
        setError("Add at least one option");
        return;
      }
      options = lines;
    }
    try {
      setError(null);
      await onSubmit({
        name: name.trim(),
        field_type: fieldType,
        applies_to: appliesTo,
        options,
      });
    } catch (e: any) {
      setError(errMsg(e, "Failed to save custom field"));
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={isEdit ? "Edit Custom Field" : "New Custom Field"}
      destroyOnClose
      maskClosable={false}
      keyboard={false}
      centered
      width="640px"
      zIndex={1001}
      styles={{
        content: { padding: 0, borderRadius: 12, overflow: "hidden" },
        header: { padding: "14px 20px 12px", borderBottom: "1px solid #f0f0f0", marginBottom: 0 },
        body: { padding: "16px 24px 20px" },
        footer: { padding: "10px 20px 14px", borderTop: "1px solid #f0f0f0", marginTop: 0 },
        mask: {
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          background: "rgba(0,0,0,0.45)",
        },
      }}
      footer={
        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-light"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-info"
            onClick={handleSubmit}
            disabled={submitting || !name.trim()}
          >
            {submitting ? "Saving…" : isEdit ? "Save Changes" : "Create Field"}
          </button>
        </div>
      }
    >
      <div className="d-flex flex-column gap-3">
        {error && (
          <div className="alert alert-danger py-2 mb-0">{error}</div>
        )}
        <div>
          <label className="form-label small fw-semibold">
            Name <span className="text-danger">*</span>
          </label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Customer"
            maxLength={64}
          />
        </div>
        <div>
          <label className="form-label small fw-semibold">Type</label>
          <Select
            style={{ width: "100%" }}
            value={fieldType}
            onChange={(v) => setFieldType(v as CustomFieldType)}
            options={FIELD_TYPE_OPTIONS}
            disabled={isEdit}
          />
          {isEdit && (
            <span className="text-muted small">
              Type cannot be changed after creation.
            </span>
          )}
        </div>
        <div>
          <label className="form-label small fw-semibold">Applies to</label>
          <Select
            style={{ width: "100%" }}
            value={appliesTo}
            onChange={(v) => setAppliesTo(v as CustomFieldAppliesTo)}
            options={APPLIES_TO_OPTIONS}
          />
        </div>
        {isOptionType && (
          <div>
            <label className="form-label small fw-semibold">
              Options (one per line)
            </label>
            <Input.TextArea
              value={optionsText}
              onChange={(e) => setOptionsText(e.target.value)}
              rows={4}
              placeholder={"Option 1\nOption 2\nOption 3"}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CustomFieldEditor;
